import * as React from "react";

import { cn } from "@/lib/utils";

type TypographySize =
  | "h1"
  | "h2"
  | "h3"
  | "sh1"
  | "sh2"
  | "sh3"
  | "b1"
  | "b2"
  | "b3"
  | "l1"
  | "l2"
  | "l3"
  | "l4";

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  size?: TypographySize;
  as?: keyof JSX.IntrinsicElements;
}

const sizeToDefaults: Record<
  TypographySize,
  { as: keyof JSX.IntrinsicElements; className: string }
> = {
  // Headings
  h1: {
    as: "h1",
    className:
      "text-[28px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
  h2: {
    as: "h2",
    className:
      "text-[24px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
  h3: {
    as: "h3",
    className:
      "text-[22px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
  // Subheadings
  sh1: {
    as: "h3",
    className:
      "text-[20px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
  sh2: {
    as: "h4",
    className:
      "text-[18px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
  sh3: {
    as: "h5",
    className:
      "text-[16px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
  // Body
  b1: {
    as: "p",
    className:
      "text-[16px] leading-[1.4] tracking-[-0.02em] font-normal text-sds-text-black",
  },
  b2: {
    as: "p",
    className:
      "text-[14px] leading-[1.4] tracking-[-0.02em] font-normal text-sds-text-black",
  },
  b3: {
    as: "p",
    className:
      "text-[12px] leading-[1.4] tracking-[-0.02em] font-normal text-sds-text-black",
  },
  // Labels
  l1: {
    as: "span",
    className:
      "text-[16px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
  l2: {
    as: "span",
    className:
      "text-[14px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
  l3: {
    as: "span",
    className:
      "text-[12px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
  l4: {
    as: "span",
    className:
      "text-[10px] leading-[1.4] tracking-[-0.04em] font-semibold text-sds-text-black",
  },
};

export const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ size = "b1", as, className, children, ...props }, ref) => {
    const defaults = sizeToDefaults[size];
    const Component = (as || defaults.as) as any;
    return (
      <Component
        ref={ref as any}
        className={cn(defaults.className, className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Typography.displayName = "Typography";

export default Typography;
